import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Flag, Loader2, AlertTriangle, ArrowUpRight, Star } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { CareerFacts } from "@/components/CareerFacts";
import { NewBadge, StarButton, CompareButton } from "@/components/CareerActions";

export default function CareerDetail() {
  const { token, careerId } = useParams();
  const [d, setD] = useState(null);
  const [selected, setSelected] = useState(false);
  const [favs, setFavs] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    http.get(`/careers/${careerId}`, { params: { token } }).then((r) => { setD(r.data); setSelected(!!r.data.selected); setFavs(r.data.favorites || []); });
  }, [careerId, token]);

  if (!d) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-[#D96B43]" /></div>;
  const { career: c, match: m } = d;

  const toggleFav = async (url) => {
    try {
      const { data } = await http.post(`/results/${token}/favorites/toggle`, { career_id: c.id, url });
      const mine = data.favorites.filter((f) => f.career_id === c.id).map((f) => f.url);
      toast.success(mine.includes(url) ? "Saved. It will appear in your PDF and emails." : "Removed from saved resources.");
      setFavs(mine);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  const choose = async () => {
    await http.post(`/results/${token}/select-career`, { career_id: c.id });
    setSelected(true);
    toast.success(`${c.title} set as your chosen path. Your report now includes its action plan.`);
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-8 pb-10">
      {token ? (
        <Link to={`/results/${token}`} data-testid="back-to-results-link" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#1C242B]">
          <ArrowLeft size={16} /> Back to results
        </Link>
      ) : (
        <Link to="/careers" data-testid="back-to-explore-link" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#1C242B]">
          <ArrowLeft size={16} /> All careers
        </Link>
      )}
      <section className="mt-6 grid grid-cols-12 gap-6 fade-up">
        <div className="col-span-12 lg:col-span-8">
          <p className="overline-label flex items-center gap-2">Career profile {c.is_new && <NewBadge testId="career-new-badge" />}</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="career-title">{c.title}</h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600">{c.summary}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <StarButton id={c.id} withLabel testId="career-star-btn" />
            <CompareButton id={c.id} withLabel testId="career-compare-btn" />
          </div>
        </div>
        {!token && (
          <div className="col-span-12 lg:col-span-4 rounded-2xl bg-[#1C242B] text-[#FAF8F5] p-6" data-testid="career-take-test-prompt">
            <p className="font-mono-ui text-xs tracking-[0.2em] text-[#F0A584]">HOW WELL DOES IT FIT YOU?</p>
            <p className="mt-3 text-[15px] leading-relaxed text-[#E2E8F0]">Take the free test to see your match score, why it aligns and which skills to build.</p>
            <Link to="/assessment" data-testid="career-take-test-btn" className="mt-5 w-full inline-flex justify-center items-center gap-2 rounded-full bg-[#D96B43] px-5 py-3 text-sm font-semibold hover:bg-[#C05630] transition-colors">
              Take the test to see your fit <ArrowUpRight size={16} />
            </Link>
          </div>
        )}
        {m && (
          <div className="col-span-12 lg:col-span-4 rounded-2xl bg-white border border-[#1C242B]/10 p-6">
            <p className="text-xs font-mono-ui text-slate-500">YOUR ALIGNMENT</p>
            <p className="font-mono-ui text-5xl font-semibold text-[#D96B43] mt-2" data-testid="career-match-score">{m.score}%</p>
            <p className="text-xs text-slate-500 mt-2">Skills &amp; interests {m.core}% · Environment fit {m.fit}%</p>
            <button data-testid="choose-career-btn" onClick={choose} disabled={selected}
              className={`mt-5 w-full inline-flex justify-center items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-colors ${selected ? "bg-[#E8F0EC] text-[#3E6B5C]" : "bg-[#1C242B] text-white hover:bg-[#D96B43]"}`}>
              {selected ? <><Check size={16} /> Your chosen path</> : <><Flag size={16} /> Choose this career</>}
            </button>
          </div>
        )}
      </section>

      {m && (
        <section className="mt-10 grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl bg-white border border-[#1C242B]/10 p-6" data-testid="alignment-reasons">
            <p className="overline-label mb-4">Why it aligns</p>
            <ul className="space-y-3 text-[15px]">{m.reasons.map((r) => <li key={r} className="pl-3 border-l-2 border-[#D96B43]">{r}</li>)}</ul>
          </div>
          <div className="rounded-2xl bg-[#F6E7DF]/60 border border-[#D96B43]/20 p-6" data-testid="skill-gaps">
            <p className="overline-label mb-4">Skills to build</p>
            {m.gaps.length ? <ul className="space-y-2 text-[15px]">{m.gaps.map((g) => <li key={g} className="flex gap-2"><AlertTriangle size={16} className="text-[#C05630] mt-0.5 shrink-0" />{g}</li>)}</ul>
              : <p className="text-[15px] text-slate-700">No major gaps detected for the core requirements. Your profile covers the essentials.</p>}
          </div>
        </section>
      )}

      <section className="mt-10">
        <p className="overline-label mb-4">The real picture</p>
        <CareerFacts career={c} />
        <div className="mt-5 rounded-2xl bg-[#1C242B] text-[#FAF8F5] p-6" data-testid="career-expectations">
          <p className="font-mono-ui text-xs tracking-[0.2em] text-[#F0A584]">REALISTIC EXPECTATIONS</p>
          <p className="mt-3 leading-relaxed">{c.expectations}</p>
        </div>
      </section>

      <section className="mt-12 grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-6">
          <p className="overline-label">Next steps · regardless of experience</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-2">How to get into {c.title.toLowerCase()}</h2>
          <ol className="mt-6 space-y-4" data-testid="career-entry-steps">
            {c.entry_steps.map((s, i) => (
              <li key={s} className="flex gap-4">
                <span className="w-8 h-8 shrink-0 rounded-full bg-[#D96B43] text-white grid place-items-center font-mono-ui text-sm">{i + 1}</span>
                <span className="pt-1 text-[15px] leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="col-span-12 lg:col-span-6 rounded-2xl bg-white border border-[#1C242B]/10 p-6">
          <p className="overline-label mb-4">Universal job-search moves</p>
          <ul className="space-y-3 text-[15px]" data-testid="universal-steps">
            {d.universal_steps.map((s) => <li key={s} className="pl-3 border-l-2 border-[#3E6B5C]">{s}</li>)}
          </ul>
        </div>
      </section>

      <section className="mt-12 grid md:grid-cols-2 gap-5" data-testid="career-learning-links">
        {[["courses", "Free learning, worldwide"], ["volunteer", "Volunteer to gain experience"]].map(([k, title]) => (
          <div key={k} className="rounded-2xl bg-white border border-[#1C242B]/10 p-6" data-testid={`career-${k}-links`}>
            <p className="overline-label mb-1">{title}</p>
            {token ? <p className="text-xs text-slate-500 mb-4">Tap the star to save a link to your PDF and emails.</p> : <div className="mb-4" />}
            <ul className="space-y-2">
              {d.learning[k].map((l) => (
                <li key={l.url} className="flex items-center gap-2">
                  {token && <button onClick={() => toggleFav(l.url)} data-testid={`fav-toggle-${k}-${d.learning[k].indexOf(l)}`} aria-label="Save resource"
                    className="p-1 rounded-full hover:bg-[#F6E7DF] transition-colors">
                    <Star size={16} className={favs.includes(l.url) ? "fill-[#D96B43] text-[#D96B43]" : "text-slate-300"} />
                  </button>}
                  <a href={l.url} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-1.5 text-[15px] text-[#1C242B] hover:text-[#D96B43] transition-colors">
                    {l.label} <ArrowUpRight size={14} className="text-slate-400 group-hover:text-[#D96B43]" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      <div className="mt-12 flex flex-wrap gap-3">
        {token ? (
          <Link to={`/results/${token}/report`} data-testid="career-open-report-btn" className="inline-flex items-center gap-2 rounded-full bg-[#D96B43] text-white px-6 py-3 text-sm font-semibold hover:bg-[#C05630] transition-colors">
            View full report <ArrowUpRight size={16} />
          </Link>
        ) : (
          <Link to="/careers" data-testid="career-explore-more-btn" className="inline-flex items-center gap-2 rounded-full border border-[#1C242B]/15 px-6 py-3 text-sm font-semibold hover:bg-[#1C242B]/5 transition-colors">
            Explore more careers
          </Link>
        )}
      </div>
    </div>
  );
}
